import { apiClient } from 'shared/api/client';
import { endpoints } from 'shared/api/endpoints';
import { mockSchools } from 'shared/api/fixtures';
import { fetchWithFallback } from 'shared/data/fetchWithFallback';
import { mapCreateSchoolInputToDb, mapDbSchool } from 'shared/data/mappers';
import {
  catalogFromLegacyConstants,
  groupSchoolCatalog,
  type CatalogCategory,
} from 'features/schools/utils/schoolCatalog';
import { resolveCatalogStatus } from 'constants/platformCatalogRegistry';
import { getSupabase } from 'shared/supabase/client';
import type { CreateSchoolInput, School, SchoolStatus } from 'types';

function mergeSchoolRecord(fixture: School, remote: School): School {
  const merged = { ...fixture, ...remote };
  if (merged.status === 'disabled') return merged;
  return {
    ...merged,
    status: resolveCatalogStatus(merged),
  };
}

function mergeSchoolCatalog(remoteSchools: School[]): School[] {
  const bySlug = new Map<string, School>();

  for (const fixture of mockSchools) {
    bySlug.set(fixture.slug, fixture);
  }

  for (const school of remoteSchools) {
    const fixture = bySlug.get(school.slug);
    if (fixture) {
      bySlug.set(school.slug, mergeSchoolRecord(fixture, school));
    } else {
      bySlug.set(school.slug, school);
    }
  }

  return Array.from(bySlug.values()).sort((a, b) => a.sortOrder - b.sortOrder);
}

async function fetchSchoolsFromApi(): Promise<School[]> {
  const { data } = await apiClient.get<School[]>(endpoints.schools.list);
  return Array.isArray(data) ? data : [];
}

async function fetchSchoolsFromSupabase(): Promise<School[]> {
  const { data, error } = await getSupabase()
    .from('core_schools')
    .select('*')
    .is('deleted_at', null)
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return (data ?? []).map((row) => mapDbSchool(row));
}

async function fetchSchoolCatalogFromSupabase(): Promise<School[]> {
  // Select all non-deleted rows and let groupSchoolCatalog decide visibility/grouping
  // client-side. We intentionally avoid filtering/ordering on is_active or category_id here
  // so the query still works if those columns haven't been migrated yet.
  const { data, error } = await getSupabase()
    .from('core_schools')
    .select('*')
    .is('deleted_at', null)
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return (data ?? []).map((row) => mapDbSchool(row));
}

async function fetchSchoolCatalogFromApi(): Promise<School[]> {
  return fetchSchoolsFromApi();
}

export async function fetchSchools(): Promise<School[]> {
  try {
    const remote = await fetchWithFallback(fetchSchoolsFromSupabase, fetchSchoolsFromApi);
    if (!remote.length) {
      return [...mockSchools].sort((a, b) => a.sortOrder - b.sortOrder);
    }
    return mergeSchoolCatalog(remote);
  } catch (err) {
    console.warn('Schools unavailable, using fixture data:', err);
    return [...mockSchools].sort((a, b) => a.sortOrder - b.sortOrder);
  }
}

export async function fetchSchoolCatalog(): Promise<CatalogCategory[]> {
  try {
    const remote = await fetchWithFallback(
      fetchSchoolCatalogFromSupabase,
      fetchSchoolCatalogFromApi,
    );
    if (!remote.length) {
      return catalogFromLegacyConstants();
    }
    return groupSchoolCatalog(remote);
  } catch (err) {
    console.warn('School catalog unavailable, using fallback data:', err);
    return catalogFromLegacyConstants();
  }
}

export async function fetchSchoolBySlug(slug: string): Promise<School> {
  const schools = await fetchSchools();
  const merged = schools.find((s) => s.slug === slug);
  if (merged) return merged;

  return fetchWithFallback(
    async () => {
      const { data, error } = await getSupabase()
        .from('core_schools')
        .select('*')
        .eq('slug', slug)
        .is('deleted_at', null)
        .single();
      if (error) throw error;
      return mapDbSchool(data);
    },
    async () => {
      const { data } = await apiClient.get<School>(endpoints.schools.detail(slug));
      return data;
    }
  );
}

export async function createSchool(input: CreateSchoolInput): Promise<School> {
  return fetchWithFallback(
    async () => {
      const { data, error } = await getSupabase()
        .from('core_schools')
        .insert(mapCreateSchoolInputToDb(input))
        .select('*')
        .single();
      if (error) throw error;
      return mapDbSchool(data);
    },
    async () => {
      const { data } = await apiClient.post<School>(endpoints.admin.schools, input);
      return data;
    }
  );
}

export async function updateSchoolStatus(id: string, status: SchoolStatus): Promise<School> {
  return fetchWithFallback(
    async () => {
      const { data, error } = await getSupabase()
        .from('core_schools')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      return mapDbSchool(data);
    },
    async () => {
      const { data } = await apiClient.patch<School>(endpoints.admin.schoolUpdate(id), { status });
      return data;
    }
  );
}
